/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SINGLE GENERIC PDF EXPORT UTILITY
 *  src/utils/pdfExportUtil.ts
 *
 *  Usage – any dashboard, any columns:
 *
 *    import { generateDashboardPdf } from 'utils/pdfExportUtil';
 *    import type { PdfColumn }       from 'utils/pdfExportUtil';
 *
 *    await generateDashboardPdf({
 *      columns:            myColumns,          // PdfColumn[]
 *      allData:            rowsFromApi,         // any[]
 *      dashboardTitle:     'My Dashboard',
 *      tableTitle:         'My Table',
 *      totalElements:      1234567,             // server total (for footer)
 *      topKpiElementId:    'my-top-kpi',        // optional DOM id
 *      bottomKpiElementId: 'my-bottom-kpi',     // optional DOM id
 *      fileNamePrefix:     'MyDashboard_Export',
 *    });
 *
 *  PdfColumn interface:
 *    key        – data-row property name
 *    label      – column header text in the PDF
 *    weight     – relative width weight (any unit, all widths are scaled to fill page)
 *    formatter  – optional (row: any) => string   (defaults to String(row[key]))
 *    isStatus   – when true, renders a coloured badge instead of plain text
 *
 *  Status colours are resolved automatically from the cell text.
 *  Pass any string label – the colour map below is intentionally broad
 *  and covers Pipeline, Incidents, Alerts, and generic good/warn/bad states.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface PdfColumn {
  /** Property key on each row object */
  key: string;
  /** Column header shown in the PDF table */
  label: string;
  /**
   * Relative width weight – all columns are scaled proportionally to fill the
   * usable page width, so the unit is irrelevant (just use any consistent scale).
   */
  weight: number;
  /** Optional cell-value formatter. Return a plain string to render. */
  formatter?: (row: any) => string;
  /**
   * When true the cell value is rendered as a coloured rounded badge.
   * The colour is looked up automatically from the STATUS_COLORS map.
   */
  isStatus?: boolean;
  /**
   * When true the cell value is rendered as a clickable hyperlink.
   */
  isLink?: boolean;
  /** Optional formatter to resolve the URL for the link. If not provided, the cell text itself is used as the URL. */
  linkFormatter?: (row: any) => string | undefined | null;
}

export interface PdfExportParams {
  /** Column definitions – fully dynamic, pass any columns you like */
  columns: PdfColumn[];
  /** Data rows (will be sliced to the maximum that fits in maxPages pages) */
  allData: any[];
  /** Large heading on page 1 */
  dashboardTitle: string;
  /** Smaller sub-heading / table section label */
  tableTitle: string;
  /** Server-side total row count shown in the footer */
  totalElements: number;
  /**
   * DOM element id(s) of the TOP KPI / tile strips to embed on page 1.
   * Pass a single string OR an array of strings – each image is stacked
   * vertically in the order provided.
   */
  topKpiElementId?: string | string[];
  /** DOM element id of the BOTTOM chart-card strip to embed on the last page */
  bottomKpiElementId?: string;
  /** Base file-name prefix – timestamp appended automatically */
  fileNamePrefix?: string;
  /** Maximum number of pages (default: 10) */
  maxPages?: number;
}

// ─── Layout constants (A4 landscape) ─────────────────────────────────────────

const PAGE_W = 297;           // mm
const PAGE_H = 210;           // mm
const MARGIN = 10;            // mm
const COL_HEADER_H = 7;       // mm – column-header row height
const ROW_H = 5.5;            // mm – data-row height
const FOOTER_H = 7;           // mm – footer strip height
const KPI_TOP_MAX_H = 30;     // mm – max height of the top KPI image
const KPI_BOTTOM_MAX_H = 30;  // mm – max height of the bottom KPI image
const HEADER_FS = 7;          // pt – column header font size
const ROW_FS = 6.5;           // pt – data row font size
const NAVY: [number, number, number] = [0, 48, 95];

// ─── Status / badge colour map ────────────────────────────────────────────────
// Add any new status labels here; they will automatically apply everywhere.

const STATUS_COLORS: Record<string, [number, number, number]> = {
  // Pipeline statuses
  SUCCESSFUL:    [1,   192,  8  ],
  SUCCEEDED:     [1,   192,  8  ],
  SUCCESS:       [1,   192,  8  ],
  FAILED:        [254, 38,   38 ],
  FAIL:          [254, 38,   38 ],
  ABORTED:       [245, 118,  0  ],
  ABORT:         [245, 118,  0  ],
  'IN PROGRESS': [236, 185,  20 ],
  INPROGRESS:    [236, 185,  20 ],
  DELAYED:       [255, 56,   176],
  SCHEDULED:     [158, 158,  158],
  // ITSM / Priority (Incidents)
  P3:            [245, 75,   60 ],
  P4:            [247, 146,  39 ],
  P5:            [241, 170,  44 ],
  // Alert Priority (matches PriorityChip in AlertsTable)
  URGENT:        [245, 75,   60 ],
  HIGH:          [247, 146,  39 ],
  MODERATE:      [241, 170,  44 ],
  LOW:           [250, 204,  50 ],
  // Alert severities
  CRITICAL:      [254, 38,   38 ],
  MAJOR:         [245, 118,  0  ],
  MINOR:         [236, 185,  20 ],
  WARNING:       [255, 193,  7  ],
  INFORMATION:   [33,  150,  243],
  CLOSED:        [76,  175,  80 ],
  // Generic
  OPEN:          [33,  150,  243],
  RESOLVED:      [76,  175,  80 ],
  ASSIGNED:      [103, 58,   183],
  NEW:           [0,   188,  212],
  PENDING:       [255, 152,  0  ],
  COMPLETE:      [76,  175,  80 ],
  INCOMPLETE:    [244, 67,   54 ],
  RCA:           [156, 39,   176],
};

const getStatusColor = (text: string): [number, number, number] =>
  STATUS_COLORS[text.toUpperCase().trim()] ?? [158, 158, 158];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Truncate text so it fits inside maxWidth mm, appending "…" if needed. */
const fitText = (pdf: jsPDF, text: string, maxWidth: number): string => {
  if (pdf.getTextWidth(text) <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && pdf.getTextWidth(t + '\u2026') > maxWidth) {
    t = t.slice(0, -1);
  }
  return t + '\u2026';
};

/** Resolve mm-widths proportionally from weight values. */
const resolveWidths = (cols: PdfColumn[]): number[] => {
  const usable = PAGE_W - 2 * MARGIN;
  const total = cols.reduce((s, c) => s + c.weight, 0);
  return cols.map(c => (c.weight / total) * usable);
};

/** Get plain string value for a cell. */
const getCellText = (row: any, col: PdfColumn): string => {
  try {
    if (col.formatter) return String(col.formatter(row) ?? '--');
    const v = row?.[col.key];
    if (v == null || v === '') return '--';
    return String(v);
  } catch {
    return '--';
  }
};

/**
 * Capture a DOM element as PNG while preserving natural aspect ratio.
 * Returns null if the element cannot be found.
 */
const captureElement = async (
  id: string,
): Promise<{ dataUrl: string; aspectRatio: number } | null> => {
  const el = document.getElementById(id);
  if (!el) return null;

  const canvas = await html2canvas(el, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    scrollY: -window.scrollY,
    ignoreElements: (e: Element) =>
      ['exclude-camera', 'exclude-export'].includes((e as HTMLElement).id),
  });
  return {
    dataUrl: canvas.toDataURL('image/png'),
    aspectRatio: canvas.width / canvas.height,
  };
};

// ─── PDF drawing helpers ──────────────────────────────────────────────────────

const drawColHeaders = (
  pdf: jsPDF,
  cols: PdfColumn[],
  widths: number[],
  y: number,
): number => {
  pdf.setFillColor(...NAVY);
  pdf.rect(MARGIN, y, PAGE_W - 2 * MARGIN, COL_HEADER_H, 'F');
  pdf.setFontSize(HEADER_FS);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  let x = MARGIN;
  cols.forEach((col, i) => {
    pdf.text(fitText(pdf, col.label, widths[i] - 2), x + 1.5, y + COL_HEADER_H / 2 + 1.2);
    x += widths[i];
  });
  return y + COL_HEADER_H;
};

const drawDataRow = (
  pdf: jsPDF,
  row: any,
  cols: PdfColumn[],
  widths: number[],
  y: number,
  rowIndex: number,
): void => {
  // Alternating row stripe
  const even = rowIndex % 2 === 0;
  pdf.setFillColor(even ? 245 : 255, even ? 247 : 255, even ? 250 : 255);
  pdf.rect(MARGIN, y, PAGE_W - 2 * MARGIN, ROW_H, 'F');

  // Hairline separator
  pdf.setDrawColor(220, 225, 230);
  pdf.setLineWidth(0.1);
  pdf.line(MARGIN, y + ROW_H, PAGE_W - MARGIN, y + ROW_H);

  let x = MARGIN;
  cols.forEach((col, i) => {
    const w = widths[i];
    const pad = 1.5;
    const text = getCellText(row, col);

    if (col.isStatus) {
      // ── Coloured badge ──────────────────────────────────────
      const [r, g, b] = getStatusColor(text);
      const badgeW = Math.min(w - 3, 22);
      const badgeX = x + (w - badgeW) / 2;
      const badgeY = y + 0.9;
      const badgeH = ROW_H - 1.8;

      pdf.setFillColor(r, g, b);
      pdf.roundedRect(badgeX, badgeY, badgeW, badgeH, 0.8, 0.8, 'F');

      pdf.setFontSize(5);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      const tw = pdf.getTextWidth(text);
      pdf.text(text, badgeX + (badgeW - tw) / 2, y + ROW_H / 2 + 0.9);

      // reset
      pdf.setFontSize(ROW_FS);
      pdf.setFont('helvetica', 'normal');
    } else if (col.isLink) {
      // ── Clickable Link ────────────────────────────────────────────
      let url = col.linkFormatter ? col.linkFormatter(row) : null;
      if (!col.linkFormatter && text !== '--' && text !== '-') {
        url = text;
      }
      
      const fittedText = fitText(pdf, text, w - 2 * pad);
      pdf.setFontSize(ROW_FS);
      pdf.setFont('helvetica', 'normal');
      
      if (url) {
        pdf.setTextColor(0, 102, 204); // blue color for links
        pdf.textWithLink(fittedText, x + pad, y + ROW_H / 2 + 1, { url });
        
        // Optional: add an underline
        const textWidth = pdf.getTextWidth(fittedText);
        pdf.setDrawColor(0, 102, 204);
        pdf.setLineWidth(0.1);
        pdf.line(x + pad, y + ROW_H / 2 + 1.2, x + pad + textWidth, y + ROW_H / 2 + 1.2);
      } else {
        pdf.setTextColor(50, 50, 50);
        pdf.text(fittedText, x + pad, y + ROW_H / 2 + 1);
      }
    } else {
      // ── Plain text ────────────────────────────────────────────
      pdf.setFontSize(ROW_FS);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(50, 50, 50);
      pdf.text(fitText(pdf, text, w - 2 * pad), x + pad, y + ROW_H / 2 + 1);
    }

    x += w;
  });
};

const drawFooter = (
  pdf: jsPDF,
  pageNum: number,
  totalPages: number,
  startIdx: number,
  endIdx: number,
  totalElements: number,
): void => {
  const fy = PAGE_H - FOOTER_H + 2;
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(120, 120, 120);

  pdf.text(
    `Showing ${startIdx.toLocaleString()} - ${endIdx.toLocaleString()} of ${totalElements.toLocaleString()} results (PDF limited to ${totalPages} pages)`,
    MARGIN,
    fy,
  );
  const ts = new Date().toLocaleString('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  pdf.text(`Generated: ${ts}`, PAGE_W / 2 - 22, fy);
  pdf.text(`Page ${pageNum} of ${totalPages}`, PAGE_W - MARGIN - 22, fy);
};

const drawTitlesAndTopKpis = (
  pdf: jsPDF,
  dashboardTitle: string,
  tableTitle: string,
  topKpis: { dataUrl: string; aspectRatio: number }[],
  startY: number
): number => {
  let y = startY;

  // Dashboard title
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...NAVY);
  pdf.text(dashboardTitle, MARGIN, y + 5);
  y += 9;

  // Top KPI strips
  for (const topKpi of topKpis) {
    const maxW = PAGE_W - 2 * MARGIN;
    let kpiH = KPI_TOP_MAX_H;
    let kpiW = kpiH * topKpi.aspectRatio;
    if (kpiW > maxW) {
      kpiW = maxW;
      kpiH = maxW / topKpi.aspectRatio;
    }
    pdf.addImage(topKpi.dataUrl, 'PNG', MARGIN, y, kpiW, kpiH);
    y += kpiH + 2;
  }
  if (topKpis.length > 0) y += 1;

  // Table sub-title
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...NAVY);
  pdf.text(tableTitle, MARGIN, y + 4);
  y += 8;

  return y;
};

interface RenderSubsequentPagesOpts {
  pdf: jsPDF;
  data: any[];
  columns: PdfColumn[];
  widths: number[];
  tableTitle: string;
  totalElements: number;
  maxPages: number;
  totalPages: number;
  rowsSubseq: number;
  rowsLastPage: number;
  bottomKpi: { dataUrl: string; aspectRatio: number } | null;
  bottomKpiW: number;
  bottomKpiH: number;
  startRowIdx: number;
  startPageNum: number;
}

const renderSubsequentPages = (opts: RenderSubsequentPagesOpts): void => {
  const {
    pdf, data, columns, widths, tableTitle, totalElements,
    maxPages, totalPages, rowsSubseq, rowsLastPage,
    bottomKpi, bottomKpiW, bottomKpiH, startRowIdx, startPageNum
  } = opts;

  let rowIdx = startRowIdx;
  let pageNum = startPageNum;

  while (rowIdx < data.length && pageNum < maxPages) {
    pdf.addPage();
    pageNum++;
    let curY = MARGIN;
    const pageStartIdx = rowIdx + 1;

    // Compact continuation header
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...NAVY);
    pdf.text(`${tableTitle} (continued)`, MARGIN, curY + 4);
    curY += 6;

    curY = drawColHeaders(pdf, columns, widths, curY);

    const isLast = pageNum === totalPages;
    const batchSize = isLast ? rowsLastPage : rowsSubseq;
    const count = Math.min(batchSize, data.length - rowIdx);

    for (let i = 0; i < count; i++) {
      drawDataRow(pdf, data[rowIdx], columns, widths, curY, rowIdx);
      curY += ROW_H;
      rowIdx++;
    }

    drawFooter(pdf, pageNum, totalPages, pageStartIdx, rowIdx, totalElements);

    // Bottom KPI on last page (natural width, left-aligned)
    if (isLast && bottomKpi) {
      pdf.addImage(bottomKpi.dataUrl, 'PNG', MARGIN, curY + 2, bottomKpiW, bottomKpiH);
    }
  }
};

// ─── Main export function ─────────────────────────────────────────────────────

export const generateDashboardPdf = async ({
  columns,
  allData,
  dashboardTitle,
  tableTitle,
  totalElements,
  topKpiElementId,
  bottomKpiElementId,
  fileNamePrefix = 'Dashboard',
  maxPages = 10,
}: PdfExportParams): Promise<void> => {

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const widths = resolveWidths(columns);

  // Normalise topKpiElementId → always an array
  let topKpiIds: string[] = [];
  if (topKpiElementId) {
    topKpiIds = Array.isArray(topKpiElementId) ? topKpiElementId : [topKpiElementId];
  }

  // Capture all KPI images in parallel (aspect-ratio preserved)
  const [topKpiResults, bottomKpi] = await Promise.all([
    Promise.all(topKpiIds.map(id => captureElement(id))),
    bottomKpiElementId ? captureElement(bottomKpiElementId) : Promise.resolve(null),
  ]);
  // Filter out any elements that couldn't be found
  const topKpis = topKpiResults.filter((k): k is NonNullable<typeof k> => k !== null);

  // ── Page 1 – title + top KPI(s) + table ────────────────────────────────────
  let y = drawTitlesAndTopKpis(pdf, dashboardTitle, tableTitle, topKpis, MARGIN);

  // ── Row capacity calculations ────────────────────────────────────────────────

  const availPage1    = PAGE_H - y - FOOTER_H;
  const rowsPage1     = Math.max(1, Math.floor((availPage1 - COL_HEADER_H) / ROW_H));

  const availSubseq   = PAGE_H - MARGIN - FOOTER_H;
  const rowsSubseq    = Math.max(1, Math.floor((availSubseq - COL_HEADER_H - 6/* cont. header */) / ROW_H));

  // Last page reserves space for bottom KPI if present.
  // Fix height, derive width from aspect ratio (fit-content – same as dashboard).
  const _bMaxW     = PAGE_W - 2 * MARGIN;
  let   bottomKpiH = bottomKpi ? KPI_BOTTOM_MAX_H : 0;
  let   bottomKpiW = bottomKpi ? bottomKpiH * bottomKpi.aspectRatio : 0;
  if (bottomKpi && bottomKpiW > _bMaxW) {
    bottomKpiW = _bMaxW;
    bottomKpiH = _bMaxW / bottomKpi.aspectRatio;
  }
  const rowsLastPage  = bottomKpi
    ? Math.max(1, Math.floor((availSubseq - COL_HEADER_H - 6 - bottomKpiH - 3) / ROW_H))
    : rowsSubseq;

  const maxRows = rowsPage1 + (maxPages - 2) * rowsSubseq + rowsLastPage;
  const data    = allData.slice(0, maxRows);

  // Pre-calc total pages
  const remainAfterP1  = Math.max(0, data.length - rowsPage1);
  const extraPages     = remainAfterP1 > 0 ? Math.ceil(remainAfterP1 / rowsSubseq) : 0;
  const totalPages     = Math.min(1 + extraPages, maxPages);

  // ── Render page 1 ────────────────────────────────────────────────────────────
  let curY    = drawColHeaders(pdf, columns, widths, y);
  let rowIdx  = 0;
  let pageNum = 1;

  const p1Count = Math.min(rowsPage1, data.length);
  for (let i = 0; i < p1Count; i++) {
    drawDataRow(pdf, data[rowIdx], columns, widths, curY, rowIdx);
    curY += ROW_H;
    rowIdx++;
  }
  drawFooter(pdf, pageNum, totalPages, 1, rowIdx, totalElements);

  // Embed bottom KPI on page 1 if all data fit (natural width, left-aligned)
  if (pageNum === 1 && rowIdx >= data.length && bottomKpi) {
    if (curY + 3 + bottomKpiH < PAGE_H - FOOTER_H) {
      pdf.addImage(bottomKpi.dataUrl, 'PNG', MARGIN, curY + 3, bottomKpiW, bottomKpiH);
    }
  }

  // ── Render subsequent pages ───────────────────────────────────────────────────
  renderSubsequentPages({
    pdf, data, columns, widths, tableTitle, totalElements,
    maxPages, totalPages, rowsSubseq, rowsLastPage,
    bottomKpi, bottomKpiW, bottomKpiH, startRowIdx: rowIdx, startPageNum: pageNum
  });

  // ── Save ──────────────────────────────────────────────────────────────────────
  const ts = new Date()
    .toLocaleString('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
    .replaceAll(/[/, :]/g, '_');

  pdf.save(`${fileNamePrefix}_${ts}.pdf`);
};
